/**
 * VulForm JavaScript SDK
 *
 * A powerful, type-safe SDK for integrating VulForm contact forms
 * into any JavaScript application.
 */

// Import shared types and utilities from core
import {
  VulFormApiClient,
  VulFormError,
  FormValidator,
  VULFORM_CONFIG,
  getDefaultConfig,
  type FormTemplate,
  type FormField,
  type FieldType,
  type VulFormProps,
  type FormError,
  type ValidationErrors,
  type ApiClientConfig,
} from '@vulform/core';

// Re-export core types for convenience
export {
  VulFormError,
  type FormTemplate,
  type FormField,
  type FieldType,
  type VulFormProps,
  type FormError,
  type ValidationErrors,
} from '@vulform/core';

export interface VulFormConfig {
  apiKey: string;
  baseUrl?: string;
  timeout?: number;
  retries?: number;
  onSuccess?: (response: SubmissionResponse) => void;
  onError?: (error: VulFormError) => void;
  onRateLimit?: (retryAfter: number) => void;
  debug?: boolean;
}

export interface FormData {
  [key: string]: string | number | boolean | File | undefined;
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export interface SubmissionResponse {
  success: boolean;
  id: string;
  message: string;
  timestamp: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

export interface AnalyticsEvent {
  type: 'form_view' | 'form_start' | 'form_abandon' | 'form_submit' | 'validation_error';
  formId?: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

export class VulForm {
  private config: Required<VulFormConfig>;
  private submitCount: number = 0;
  private lastSubmitTime: number = 0;
  private apiClient: VulFormApiClient;

  constructor(config: VulFormConfig) {
    if (!config.apiKey) {
      throw new VulFormError('API key is required', 'MISSING_API_KEY');
    }

    if (!config.apiKey.startsWith('vf_')) {
      throw new VulFormError('Invalid API key format', 'INVALID_API_KEY');
    }

    // Get default configuration from shared config
    const defaultConfig = getDefaultConfig();

    this.config = {
      baseUrl: defaultConfig.baseUrl,
      timeout: defaultConfig.timeout,
      retries: defaultConfig.retries,
      debug: false,
      onSuccess: () => {},
      onError: () => {},
      onRateLimit: () => {},
      ...config,
    };

    // Initialize core utilities
    this.apiClient = new VulFormApiClient({
      apiKey: this.config.apiKey,
      baseUrl: this.config.baseUrl,
      timeout: this.config.timeout,
    });

    if (this.config.debug) {
      console.log('[VulForm] SDK initialized with config:', {
        apiKey: config.apiKey.substring(0, 8) + '...',
        baseUrl: this.config.baseUrl,
      });
    }
  }

  /**
   * Submit a form with automatic retries and rate limiting
   */
  async submit(data: FormData): Promise<SubmissionResponse> {
    this.validateFormData(data);

    const now = Date.now();

    // Rate limiting check (client-side)
    if (this.submitCount > 0 && now - this.lastSubmitTime < 1000) {
      throw new VulFormError('Rate limit exceeded', 'RATE_LIMIT_EXCEEDED');
    }

    this.trackEvent({
      type: 'form_submit',
      timestamp: now,
      metadata: { fieldCount: Object.keys(data).length },
    });

    let lastError: VulFormError | null = null;

    for (let attempt = 1; attempt <= this.config.retries; attempt++) {
      try {
        const response = await this.makeRequest('/api/v1/submit', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.config.apiKey}`,
            'X-VulForm-SDK': `js/${VULFORM_CONFIG.SDK_VERSION}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          if (response.status === 429) {
            const retryAfter = parseInt(response.headers.get('Retry-After') || '60');
            this.config.onRateLimit(retryAfter);
            throw new VulFormError('Rate limit exceeded', 'RATE_LIMIT_EXCEEDED', 429, {
              retryAfter,
            });
          }

          throw new VulFormError(
            errorData.message || 'Submission failed',
            errorData.code || 'SUBMISSION_FAILED',
            response.status,
            errorData
          );
        }

        const result: SubmissionResponse = await response.json();

        this.submitCount++;
        this.lastSubmitTime = now;

        this.config.onSuccess(result);

        if (this.config.debug) {
          console.log('[VulForm] Submission successful:', result);
        }

        return result;
      } catch (error) {
        lastError =
          error instanceof VulFormError
            ? error
            : new VulFormError(
                error instanceof Error ? error.message : 'Unknown error',
                'NETWORK_ERROR'
              );

        if (attempt === this.config.retries) {
          this.config.onError(lastError);
          throw lastError;
        }

        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  /**
   * Validate form data before submission
   */
  validate(data: FormData): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Basic required field validation
    if (!data.email) {
      errors.push({
        field: 'email',
        message: 'Email is required',
        code: 'REQUIRED_FIELD',
      });
    } else if (!this.isValidEmail(data.email.toString())) {
      errors.push({
        field: 'email',
        message: 'Please enter a valid email address',
        code: 'INVALID_EMAIL',
      });
    }

    if (!data.message) {
      errors.push({
        field: 'message',
        message: 'Message is required',
        code: 'REQUIRED_FIELD',
      });
    }

    // Warnings for better UX
    if (!data.name) {
      warnings.push({
        field: 'name',
        message: 'Name is recommended for better user experience',
        suggestion: 'Consider making the name field required',
      });
    }

    if (data.message && data.message.toString().length < 10) {
      warnings.push({
        field: 'message',
        message: 'Message seems quite short',
        suggestion: 'Consider providing more details',
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Real-time field validation
   */
  async validateField(fieldName: string, value: any): Promise<ValidationError | null> {
    switch (fieldName) {
      case 'email':
        if (!value) return { field: 'email', message: 'Email is required', code: 'REQUIRED_FIELD' };
        if (!this.isValidEmail(value))
          return { field: 'email', message: 'Invalid email format', code: 'INVALID_EMAIL' };
        break;

      case 'message':
        if (!value)
          return { field: 'message', message: 'Message is required', code: 'REQUIRED_FIELD' };
        if (value.length < 5)
          return { field: 'message', message: 'Message too short', code: 'MIN_LENGTH' };
        break;
    }

    return null;
  }

  /**
   * Track analytics events
   */
  trackEvent(event: AnalyticsEvent): void {
    if (this.config.debug) {
      console.log('[VulForm] Analytics event:', event);
    }

    // Store events in localStorage for batching
    const events = this.getStoredEvents();
    events.push(event);
    localStorage.setItem('vulform_events', JSON.stringify(events));

    // Send events in batches
    if (events.length >= 10) {
      this.flushEvents();
    }
  }

  /**
   * Setup form with automatic tracking
   */
  setupForm(formElement: HTMLFormElement): VulFormFormHandler {
    return new VulFormFormHandler(this, formElement);
  }

  /**
   * Utilities
   */
  private validateFormData(data: FormData): void {
    if (!data || typeof data !== 'object') {
      throw new VulFormError('Form data must be an object', 'INVALID_DATA');
    }

    // Check for potential spam patterns
    const messageText = String(data.message || '');
    if (messageText.length > 10000) {
      throw new VulFormError('Message too long', 'MESSAGE_TOO_LONG');
    }

    // Check for excessive URLs (basic spam detection)
    const urlCount = (messageText.match(/https?:\/\//g) || []).length;
    if (urlCount > 3) {
      throw new VulFormError('Too many URLs in message', 'SUSPICIOUS_CONTENT');
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private async makeRequest(endpoint: string, options: RequestInit): Promise<Response> {
    const url = `${this.config.baseUrl}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new VulFormError('Request timeout', 'TIMEOUT');
      }

      throw error;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private getStoredEvents(): AnalyticsEvent[] {
    try {
      const stored = localStorage.getItem('vulform_events');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private async flushEvents(): Promise<void> {
    const events = this.getStoredEvents();
    if (events.length === 0) return;

    try {
      await this.makeRequest('/api/v1/analytics/events', {
        method: 'POST',
        body: JSON.stringify({ events }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.config.apiKey}`,
        },
      });

      localStorage.removeItem('vulform_events');
    } catch (error) {
      if (this.config.debug) {
        console.warn('[VulForm] Failed to send analytics events:', error);
      }
    }
  }
}

/**
 * Form handler for automatic form enhancement
 */
export class VulFormFormHandler {
  private vulform: VulForm;
  private form: HTMLFormElement;
  private submitButton?: HTMLButtonElement;
  private originalSubmitText?: string;

  constructor(vulform: VulForm, form: HTMLFormElement) {
    this.vulform = vulform;
    this.form = form;
    this.setupFormHandling();
  }

  private setupFormHandling(): void {
    // Find submit button
    this.submitButton =
      this.form.querySelector('button[type="submit"]') ||
      (this.form.querySelector('input[type="submit"]') as HTMLButtonElement);

    if (this.submitButton) {
      this.originalSubmitText =
        this.submitButton.textContent || this.submitButton.value || 'Submit';
    }

    // Track form view
    this.vulform.trackEvent({
      type: 'form_view',
      timestamp: Date.now(),
    });

    // Track form interaction
    this.form.addEventListener(
      'focusin',
      () => {
        this.vulform.trackEvent({
          type: 'form_start',
          timestamp: Date.now(),
        });
      },
      { once: true }
    );

    // Handle form submission
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Setup real-time validation
    this.setupRealTimeValidation();
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const formData = new FormData(this.form);
    const data: Record<string, any> = {};

    formData.forEach((value, key) => {
      data[key] = value;
    });

    // Validate before submission
    const validation = this.vulform.validate(data);
    if (!validation.valid) {
      this.showValidationErrors(validation.errors);
      return;
    }

    this.setLoading(true);

    try {
      const response = await this.vulform.submit(data);
      this.showSuccess(response);
      this.form.reset();
    } catch (error) {
      this.showError(error as VulFormError);
    } finally {
      this.setLoading(false);
    }
  }

  private setupRealTimeValidation(): void {
    const emailInput = this.form.querySelector(
      'input[type="email"], input[name="email"]'
    ) as HTMLInputElement;
    const messageInput = this.form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;

    if (emailInput) {
      emailInput.addEventListener('blur', async () => {
        const error = await this.vulform.validateField('email', emailInput.value);
        this.showFieldError(emailInput, error);
      });
    }

    if (messageInput) {
      messageInput.addEventListener('blur', async () => {
        const error = await this.vulform.validateField('message', messageInput.value);
        this.showFieldError(messageInput, error);
      });
    }
  }

  private setLoading(loading: boolean): void {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      this.submitButton.textContent = loading ? 'Sending...' : this.originalSubmitText!;
    }
  }

  private showSuccess(response: SubmissionResponse): void {
    this.showMessage('Thank you! Your message has been sent successfully.', 'success');
  }

  private showError(error: VulFormError): void {
    let message = 'Sorry, there was an error sending your message. Please try again.';

    if (error.code === 'RATE_LIMIT_EXCEEDED') {
      message = 'Please wait a moment before submitting again.';
    } else if (error.code === 'VALIDATION_ERROR') {
      message = 'Please check your form and try again.';
    }

    this.showMessage(message, 'error');
  }

  private showValidationErrors(errors: ValidationError[]): void {
    errors.forEach(error => {
      const field = this.form.querySelector(`[name="${error.field}"]`) as HTMLElement;
      this.showFieldError(field, error);
    });
  }

  private showFieldError(field: HTMLElement, error: ValidationError | null): void {
    const existingError = field.parentElement?.querySelector('.vulform-error');
    if (existingError) {
      existingError.remove();
    }

    if (error) {
      const errorElement = document.createElement('div');
      errorElement.className = 'vulform-error';
      errorElement.style.color = 'red';
      errorElement.style.fontSize = '14px';
      errorElement.style.marginTop = '4px';
      errorElement.textContent = error.message;

      field.parentElement?.appendChild(errorElement);
      field.style.borderColor = 'red';
    } else {
      field.style.borderColor = '';
    }
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    const existingMessage = this.form.querySelector('.vulform-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    const messageElement = document.createElement('div');
    messageElement.className = 'vulform-message';
    messageElement.style.padding = '12px';
    messageElement.style.borderRadius = '4px';
    messageElement.style.marginTop = '16px';
    messageElement.style.backgroundColor = type === 'success' ? '#d4edda' : '#f8d7da';
    messageElement.style.color = type === 'success' ? '#155724' : '#721c24';
    messageElement.style.border = `1px solid ${type === 'success' ? '#c3e6cb' : '#f5c6cb'}`;
    messageElement.textContent = message;

    this.form.appendChild(messageElement);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      messageElement.remove();
    }, 5000);
  }
}

// Default export for convenience
export default VulForm;
